import React, { useCallback, useState } from "react"

import { COLOR_PALETTE } from "@/constants/color-palette"
import {
  Input,
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@coval/ui"
import { Tooltip } from "@coval/ui/tooltip"

interface ColorGridPickerProps {
  /** Current hex color value (e.g., "#1B9E77") */
  value: string
  /** Callback fired when user selects a new color */
  onChange: (color: string) => void
  /** Accessible label for the color picker button */
  label?: string
}

/**
 * Color picker component with a grid of colorblind-friendly swatches
 *
 * Displays a popover with 50 carefully-selected colors organized in a 10×5
 * grid. The palette is based on Okabe-Ito and Paul Tol color schemes,
 * ensuring accessibility for users with color vision deficiencies.
 */
export const ColorGridPicker: React.FC<ColorGridPickerProps> = ({
  value,
  onChange,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hexInput, setHexInput] = useState(
    value.replace(/^#/, "").toUpperCase(),
  )

  // Sync local input when the popover opens or the parent value changes while closed
  const prevValueRef = React.useRef(value)
  React.useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value
      setHexInput(value.replace(/^#/, "").toUpperCase())
    }
  }, [value])

  const handleColorSelect = useCallback(
    (color: string) => {
      onChange(color)
      setHexInput(color.replace(/^#/, "").toUpperCase())
      setIsOpen(false)
    },
    [onChange],
  )

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip content={label || "Select color"}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-10 h-10 rounded-md border-2 border-coval-border
              cursor-pointer shrink-0 transition-all hover:scale-105
              hover:border-coval-border-active"
            style={{ backgroundColor: value }}
            aria-label={label || "Select color"}
          />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="start" sideOffset={8}>
        <div className="flex flex-col gap-2">
          <PopoverTitle className="mb-1 p-0">Choose a color</PopoverTitle>
          <div
            className="grid gap-1.5"
            style={{
              gridTemplateColumns: "repeat(10, 1fr)",
              width: "280px",
            }}
          >
            {COLOR_PALETTE.map((color) => (
              <Tooltip key={color} content={color}>
                <button
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  className={`
                    w-6 h-6 rounded cursor-pointer transition-all
                    hover:scale-110 hover:shadow-md
                    ${value.toLowerCase() === color.toLowerCase() ? "ring-2 ring-coval-info-border scale-110 shadow-md" : ""}
                  `}
                  style={{ backgroundColor: color }}
                  aria-label={`Select color ${color}`}
                />
              </Tooltip>
            ))}
          </div>
          <div className="pt-1">
            <Input
              type="text"
              startAdornment="#"
              className="font-mono"
              value={hexInput}
              onChange={(e) => {
                const raw = e.target.value
                  .replace(/[^0-9a-fA-F]/g, "")
                  .slice(0, 6)
                setHexInput(raw.toUpperCase())
                if (raw.length === 6) {
                  onChange(`#${raw}`)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && /^[0-9a-fA-F]{6}$/.test(hexInput)) {
                  handleColorSelect(`#${hexInput}`)
                }
              }}
              maxLength={6}
              placeholder="6366F1"
              aria-label="Custom hex color code"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
